<template>
  <v-app class="dealer-portal">
    <v-overlay :value="loader">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Dealer Portal Header -->
    <HeaderDealer />

    <v-main class="auth-pages" style="background-color: #FAC500;">
      <!-- Hero Section -->
      <section class="hero-section mt-10">
        <v-container fluid class="hero-container">
          <div class="hero-content-wrapper">
            <!-- Commission Info at Top -->
            <div class="commission-header">
              <h2 class="commission-title">
                Agents earn commissions<br>
                on every <em>sale</em>
              </h2>
              <p class="commission-example">(Example $385)</p>
            </div>

            <!-- Car Image in Center -->
            <div class="car-display-center">
              <img src="@/assets/img/tesla-model-s.png" alt="Premium Tesla" class="hero-car-image" />
            </div>

            <!-- Main Content Below -->
            <div class="hero-main-content">
              <h1 class="hero-title">
                Power Your Dealership.<br>
                Expand Your Reach.
              </h1>
              <p class="hero-subtitle">
                Join AutoSyncX™ to put your cars in front<br>
                of 1,000s of referral agents.
              </p>
              <div class="hero-buttons">
                <v-btn
                  class="hero-cta-btn"
                  :large="!$vuetify.breakpoint.xs"
                  :small="$vuetify.breakpoint.xs"
                  dark
                  @click="signUpDealership"
                >
                  Sign Up Your Dealership
                </v-btn>
              </div>
            </div>
          </div>
        </v-container>
      </section>

      <!-- Features Section -->
      <section class="features-section">
        <v-container>
          <v-row class="features-row">
            <v-col cols="12" sm="6" md="3" v-for="feature in features" :key="feature.id" class="feature-col">
              <div class="feature-card">
                <div class="feature-icon">
                  <i class="material-icons-round" :style="`color: ${feature.iconColor}; font-size: 48px;`">{{ feature.icon }}</i>
                </div>
                <h3 class="feature-title">{{ feature.title }}</h3>
                <p class="feature-description">{{ feature.description }}</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </section>

      <!-- How It Works Section -->
      <section id="commission-section" class="how-it-works-section">
        <v-container>
          <div class="section-header">
            <h2 class="section-title">How It Works</h2>
            <p class="section-subtitle">Simple steps to grow your dealership with our referral network</p>
          </div>
          <v-row class="steps-row">
            <v-col cols="12" lg="6" class="steps-col">
              <div class="step" v-for="step in steps" :key="step.number">
                <div class="step-icon-wrapper">
                  <div class="step-number">{{ step.number }}</div>
                  <div class="step-icon">
                    <i class="material-icons-round">{{ step.icon }}</i>
                  </div>
                </div>
                <div class="step-content">
                  <h3 class="step-title">{{ step.title }}</h3>
                  <p class="step-description">{{ step.description }}</p>
                </div>
              </div>
            </v-col>
            <v-col cols="12" lg="6" class="commission-transparency-col">
              <div class="commission-transparency">
                <div class="transparency-header">
                  <h3 class="transparency-title">Commission Breakdown</h3>
                  <p class="transparency-subtitle">Transparent pricing for every sale</p>
                </div>

                <div class="commission-calculator">
                  <div class="calculator-input">
                    <label class="input-label">Vehicle Sale Price</label>
                    <div class="price-display">$25,000</div>
                  </div>

                  <div class="commission-breakdown">
                    <div class="breakdown-item dealer-item">
                      <div class="breakdown-info">
                        <span class="breakdown-label">Dealer Keeps</span>
                      </div>
                      <span class="breakdown-value">$24,500</span>
                    </div>

                    <div class="breakdown-item platform-item">
                      <div class="breakdown-info">
                        <span class="breakdown-label">AutoSyncX™ Fee</span>
                      </div>
                      <span class="breakdown-value">$500</span>
                    </div>

                    <div class="breakdown-item agent-item">
                      <div class="breakdown-info">
                        <span class="breakdown-label">Agent Commission</span>
                      </div>
                      <span class="breakdown-value">$385</span>
                    </div>
                      <div class="breakdown-item agent-item">
                      <div class="breakdown-info">
                        <span class="breakdown-label">Referral Bonus</span>
                      </div>
                      <span class="breakdown-value">$15</span>
                    </div>
                      <div class="breakdown-item agent-item">
                      <div class="breakdown-info">
                        <span class="breakdown-label">AutoSyncX™ Hub</span>
                      </div>
                      <span class="breakdown-value">$100</span>
                    </div>
                  </div>

                  <div class="total-summary">
                    <div class="summary-row">
                      <span class="summary-label">Total Commission Paid</span>
                      <span class="summary-value">$500</span>
                    </div>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </section>

      <!-- Professional CTA Section -->
      <section class="cta-section">
        <v-container>
          <div class="cta-content">
            <div class="cta-text">
              <h2 class="cta-title">Ready to Transform Your Dealership?</h2>
              <p class="cta-subtitle">Join thousands of dealers who've increased their sales by 40% with our platform</p>
              <div class="cta-features">
                <div class="cta-feature">
                  <i class="material-icons-round">verified</i>
                  <span>No Setup Fees</span>
                </div>
                <div class="cta-feature">
                  <i class="material-icons-round">schedule</i>
                  <span>5-Minute Setup</span>
                </div>
                <div class="cta-feature">
                  <i class="material-icons-round">support_agent</i>
                  <span>24/7 Support</span>
                </div>
              </div>
            </div>
            <div class="cta-actions">
              <v-btn
                class="cta-primary-btn"
                :size="$vuetify.breakpoint.xs ? 'default' : 'large'"
                elevation="0"
                @click="signUpDealership"
              >
                Start Free Trial
                <i class="material-icons-round">arrow_forward</i>
              </v-btn>
              <v-btn
                class="cta-secondary-btn"
                variant="outlined"
                :size="$vuetify.breakpoint.xs ? 'default' : 'large'"
                @click="handleScheduleDemo"
                :loading="scheduleDemoLoading"
              >
                Schedule Demo
              </v-btn>
            </div>
          </div>
        </v-container>
      </section>

      <!-- Testimonials Section -->
      <section class="testimonials-section">
        <v-container>
          <v-row class="testimonials-row">
            <v-col cols="12" sm="6" lg="6" v-for="testimonial in testimonials" :key="testimonial.id" class="testimonial-col">
              <div class="testimonial-card">
                <div class="testimonial-header">
                  <div class="testimonial-avatar">
                    <img :src="testimonial.avatar" :alt="testimonial.name" />
                  </div>
                  <div class="testimonial-info">
                    <h4 class="testimonial-name">{{ testimonial.name }}</h4>
                    <div class="testimonial-rating">
                      <i v-for="star in 5" :key="star" class="material-icons-round" style="color: orange; font-size: 16px;">
                        {{ star <= testimonial.rating ? 'star' : 'star_border' }}
                      </i>
                    </div>
                  </div>
                </div>
                <p class="testimonial-text">{{ testimonial.text }}</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </section>

      <!-- Contact Section -->
      <section class="contact-section">
        <v-container>
          <div class="contact-content">
            <h2 class="contact-title" style="color: white;">CONTACT</h2>
            <div class="contact-buttons">
              <v-btn
                class="contact-btn call-btn"
                :large="!$vuetify.breakpoint.xs"
                :small="$vuetify.breakpoint.xs"
                @click="handleCallAgent"
                :loading="callAgentLoading"
                :disabled="false"
              >
                <i class="material-icons-round btn-icon">phone</i>
                Call Agent
              </v-btn>
              <v-btn
                class="contact-btn chat-btn"
                :large="!$vuetify.breakpoint.xs"
                :small="$vuetify.breakpoint.xs"
                @click="handleChatAgent"
                :loading="chatAgentLoading"
              >
                <i class="material-icons-round btn-icon">chat</i>
                Chat Agent
              </v-btn>
            </div>

            <!-- Voice Agent Status -->
            <div v-if="!hasVoiceAgent" class="voice-agent-status">
              <p class="status-text">
                <i class="material-icons-round status-icon">info</i>
                Voice agent is being configured. Chat agent is available for immediate assistance.
              </p>
            </div>
            <div class="quick-links">
              <h3 class="quick-links-title" style="color: white;">QUICK LINKS</h3>
              <div class="quick-links-list">
                <a href="/dealers" class="quick-link">Home</a>
                <a href="#" @click.prevent="scrollToSection('features')" class="quick-link">Features</a>
                <a href="#" @click.prevent="scrollToSection('how-it-works')" class="quick-link">Pricing</a>
                <a href="/dealers/about" class="quick-link">About Us</a>
              </div>
            </div>
            <div class="services-links">
              <h3 class="services-links-title">SERVICES</h3>
              <div class="services-links-list">
                <span class="service-link">Inventory Sync</span>
              </div>
            </div>
          </div>
        </v-container>
      </section>
    </v-main>

    <!-- Dealer Portal Footer -->
    <FooterDealer />

    <!-- Dealer Agent Modal -->
    <DealerAgentModal
      v-if="showAgentModal"
      :mode="agentMode"
      :agent-data="voiceAgentData"
      @close="closeAgentModal"
    />

    <!-- Demo Scheduling Modal -->
    <DemoSchedulingModal
      v-if="showDemoModal"
      @close="closeDemoModal"
    />
  </v-app>
</template>

<script>
import HeaderDealer from '@/components/Layout/HeaderDealer.vue'
import FooterDealer from '@/components/Layout/FooterDealer.vue'
import DealerAgentModal from '@/components/Dealers/DealerAgentModal.vue'
import DemoSchedulingModal from '@/components/Dealers/DemoSchedulingModal.vue'

export default {
  name: "DealerPortalHome",
  components: {
    HeaderDealer,
    FooterDealer,
    DealerAgentModal,
    DemoSchedulingModal
  },
  data() {
    return {
      loader: false,
      // Voice Agent Properties
      hasVoiceAgent: false,
      voiceAgentData: null,
      callAgentLoading: false,
      chatAgentLoading: false,

      // Modal Properties
      showAgentModal: false,
      agentMode: 'chat', // 'chat' or 'voice'
      showDemoModal: false,
      scheduleDemoLoading: false,
      features: [
        {
          id: 1,
          icon: 'account_balance_wallet',
          iconColor: '#2196F3',
          title: 'Set Your Commission Split',
          description: 'Choose your percetage split wih agents. Full flexibility'
        },
        {
          id: 2,
          icon: 'location_on',
          iconColor: '#4CAF50',
          title: 'Nationwide Agent Network',
          description: 'Tap into thousands of referral agents ready to move your inventory'
        },
        {
          id: 3,
          icon: 'sync',
          iconColor: '#FF9800',
          title: 'Easy Inventory Sync',
          description: 'API or scraping integration'
        },
        {
          id: 4,
          icon: 'trending_up',
          iconColor: '#9C27B0',
          title: 'Track payouts in dashboard',
          description: 'Full transparency'
        }
      ],
      steps: [
        {
          number: 1,
          title: 'Sign up your dealership',
          description: 'Set your commission structure and get verified in minutes. Start connecting with our network of professional agents.',
          icon: 'store'
        },
        {
          number: 2,
          title: 'Agents share your cars',
          description: 'Our network of verified agents promotes your inventory using digital tools and referral cards to qualified buyers.',
          icon: 'share'
        },
        {
          number: 3,
          title: 'Buyers close deals',
          description: 'Complete sales seamlessly and automatically pay commissions to referring agents through our transparent system.',
          icon: 'handshake'
        }
      ],
      testimonials: [
        {
          id: 1,
          name: 'Laura M.',
          avatar: require("@/assets/img/marie.jpg"),
          rating: 5,
          text: 'Our dealership gained 20 new leads in week one through AutoSyncX™'
        },
        {
          id: 2,
          name: 'Jack R.',
          avatar: require("@/assets/img/bruce-mars.jpg"),
          rating: 5,
          text: 'AutoSyncX™ helped us move aged inventory faster.'
        }
      ]
    }
  },
  mounted() {
    // Handle hash scrolling when component is mounted
    if (this.$route.hash === '#commission-section') {
      this.$nextTick(() => {
        const element = document.getElementById('commission-section');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }

    // Load dealer voice agent data
    this.loadDealerVoiceAgent();
  },
  methods: {
    signUpDealership() {
      // Navigate to dealer signup and scroll to top
      this.$router.push('/dealers/signup');
      // Scroll to top after navigation
      this.$nextTick(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    },

    loginDealership() {
      // Navigate to dealer login and scroll to top
      this.$router.push('/dealers/login');
      // Scroll to top after navigation
      this.$nextTick(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    },

    scrollToSection(sectionId) {
      this.$nextTick(() => {
        const element = document.querySelector(`.${sectionId}-section`);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    },

    // Load dealer voice agent configuration
    async loadDealerVoiceAgent() {
      try {
        console.log('🎤 Loading dealer voice agent configuration...');

        // Call API to check if dealer voice agent is configured
        const response = await axios.get('/api/dealer/voice-agent/check-availability');

        if (response.data.flag === 1 && response.data.data) {
          this.voiceAgentData = response.data.data;
          this.hasVoiceAgent = response.data.data.has_voice_agent && response.data.data.voice_agent_enabled;

          console.log('✅ Dealer voice agent loaded:', {
            hasVoiceAgent: this.hasVoiceAgent,
            agentId: this.voiceAgentData.retell_agent_id
          });
        } else {
          console.log('ℹ️ No dealer voice agent configured');
          this.hasVoiceAgent = false;
        }
      } catch (error) {
        console.error('❌ Error loading dealer voice agent:', error);
        this.hasVoiceAgent = false;
      }
    },

    // Handle Call Agent button click
    async handleCallAgent() {
      this.callAgentLoading = true;

      try {
        // Open modal in voice mode (will fallback to chat for now)
        this.agentMode = 'voice';
        this.showAgentModal = true;
        this.$toast.success('Opening conversation with Alex, your AutoSyncX™ representative...');

      } catch (error) {
        console.error('❌ Error opening agent modal:', error);
        this.$toast.error('Failed to open conversation. Please try again.');
      } finally {
        this.callAgentLoading = false;
      }
    },



    // Handle Chat Agent button click
    async handleChatAgent() {
      this.chatAgentLoading = true;

      try {
        // Open modal in chat mode
        this.agentMode = 'chat';
        this.showAgentModal = true;
        this.$toast.success('Opening chat with Alex, your AutoSyncX™ representative...');

      } catch (error) {
        console.error('❌ Error starting chat:', error);
        this.$toast.error('Failed to start chat. Please try again.');
      } finally {
        this.chatAgentLoading = false;
      }
    },



    // Close agent modal
    closeAgentModal() {
      this.showAgentModal = false;
      this.agentMode = 'chat';
    },

    // Handle Schedule Demo button click
    async handleScheduleDemo() {
      this.scheduleDemoLoading = true;

      try {
        // Open demo scheduling modal
        this.showDemoModal = true;
        this.$toast.success('Opening demo scheduling assistant...');

      } catch (error) {
        console.error('❌ Error opening demo scheduling:', error);
        this.$toast.error('Failed to open demo scheduling. Please try again.');
      } finally {
        this.scheduleDemoLoading = false;
      }
    },

    // Close demo modal
    closeDemoModal() {
      this.showDemoModal = false;
    }
  }
}
</script>

<style scoped>
.dealer-portal {
  background: #ffffff;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 400;
  line-height: 1.6;
  color: #2c3e50;
  font-size: 16px;
}

.dealer-portal h1 {
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  line-height: 1.2;
  color: #1a202c;
  letter-spacing: -0.025em;
  margin-bottom: 1rem;
}

.dealer-portal h2 {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  line-height: 1.3;
  color: #1a202c;
  letter-spacing: -0.02em;
  margin-bottom: 0.75rem;
}

.dealer-portal h3 {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  line-height: 1.4;
  color: #1a202c;
  letter-spacing: -0.015em;
  margin-bottom: 0.5rem;
}

.dealer-portal h4, .dealer-portal h5, .dealer-portal h6 {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  line-height: 1.4;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.dealer-portal p {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  line-height: 1.7;
  color: #4a5568;
  margin-bottom: 1rem;
}

.dealer-portal .text-muted {
  color: #6b7280 !important;
}

.dealer-portal .text-primary {
  color: #3b82f6 !important;
}

.dealer-portal .text-success {
  color: #059669 !important;
}

.dealer-portal-main {
  padding: 0;
  margin: 0;
}

/* Hero Section */
.hero-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: auto;
  display: flex;
  align-items: center;
  color: #2c3e50;
  position: relative;
  overflow: hidden;
  margin: 0;
  padding: 0;
}

.hero-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px;
}

.hero-content-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-height: 80vh;
  justify-content: center;
  gap: 40px;
}

/* Commission Header Styles */
.commission-header {
  margin-bottom: 20px;
}

.commission-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1.2;
  margin-bottom: 10px;
  font-family: 'Inter', sans-serif;
}

.commission-title em {
  font-style: italic;
  color: #2c3e50;
}

.commission-example {
  font-size: 1.5rem;
  color: #007bff!important;
  font-weight: 600;
  margin: 0;
  font-family: 'Inter', sans-serif;
}

/* Car Display Center */
.car-display-center {
  margin: 0px 0;
}

.hero-car-image {
  max-width: 500px;
  width: 100%;
  height: auto;
  object-fit: contain;
}

/* Main Content Styles */
.hero-main-content {
  max-width: 600px;
}

.hero-title {
  font-size: 3.2rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 24px;
  color: #1a202c;
  font-family: 'Inter', sans-serif;
  letter-spacing: -0.02em;
}

.hero-subtitle {
  font-size: 1.25rem;
  margin-bottom: 32px;
  color: #4a5568;
  line-height: 1.6;
  font-weight: 400;
  max-width: 520px;
  font-family: 'Inter', sans-serif;
}

.hero-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  align-items: center;
}

.hero-cta-btn {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%) !important;
  color: white !important;
  padding: 16px 32px !important;
  font-size: 1.1rem !important;
  font-weight: 700 !important;
  border-radius: 50px !important;
  text-transform: none !important;
  box-shadow: 0 8px 25px rgba(0, 123, 255, 0.3) !important;
  transition: all 0.3s ease !important;
}

.hero-cta-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 12px 35px rgba(0, 123, 255, 0.4) !important;
}

.hero-login-btn {
  border: 2px solid rgba(255, 255, 255, 0.9) !important;
  color: white !important;
  padding: 16px 32px !important;
  font-size: 1.1rem !important;
  font-weight: 700 !important;
  border-radius: 50px !important;
  text-transform: none !important;
  transition: all 0.3s ease !important;
  background: rgba(255, 255, 255, 0.1) !important;
}

.hero-login-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  transform: translateY(-2px) !important;
}

.hero-visual {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.hero-visual-container {
  position: relative;
  height: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 10px;
}

/* Big Car Display */
.big-car-display {
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
  border-radius: 20px;
  padding: 25px;
  text-align: center;
  position: relative;
  overflow: hidden;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.car-showcase {
  width: 100%;
  max-width: 500px;
}

.showcase-car-image {
  width: 100%;
  max-width: 380px;
  height: auto;
  filter: drop-shadow(0 15px 30px rgba(0, 0, 0, 0.15));
  transition: transform 0.3s ease;
  margin-bottom: 20px;
}

.showcase-car-image:hover {
  transform: scale(1.02);
}

.commission-info {
  text-align: center;
}

.commission-info h3 {
  font-size: 1.3rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 10px;
  line-height: 1.3;
}

.commission-amount {
  font-size: 3rem;
  font-weight: 700;
  color: #3b82f6;
  margin-bottom: 5px;
  line-height: 1;
}

.commission-info p {
  font-size: 1.1rem;
  color: #6b7280;
  margin: 0;
}





/* Features Section */
.features-section {
  padding: 100px 0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  margin: 0;
  position: relative;
}

.features-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="%23e2e8f0" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
  opacity: 0.3;
  pointer-events: none;
}

.features-section .v-container {
  position: relative;
  z-index: 1;
}

.features-row {
  margin: 0;
  padding-top: 40px;
}

.feature-col {
  padding: 15px;
}

.feature-card {
  text-align: center;
  padding: 50px 30px;
  height: 100%;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #e2e8f0;
  position: relative;
  overflow: hidden;
}

.feature-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  transform: scaleX(0);
  transition: transform 0.4s ease;
}

.feature-card:hover::before {
  transform: scaleX(1);
}

.feature-card:hover {
  transform: translateY(-12px);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  border-color: #3b82f6;
}

.feature-icon {
  margin-bottom: 32px;
  display: flex;
  justify-content: center;
  position: relative;
}

.feature-icon::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(29, 78, 216, 0.1));
  border-radius: 50%;
  z-index: -1;
  transition: all 0.3s ease;
}

.feature-card:hover .feature-icon::after {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(29, 78, 216, 0.2));
}

.feature-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 20px;
  color: #1a202c;
  font-family: 'Inter', sans-serif;
  letter-spacing: -0.01em;
}

.feature-description {
  color: #4a5568;
  line-height: 1.7;
  font-size: 1rem;
  font-weight: 400;
  max-width: 280px;
  margin: 0 auto;
}

/* How It Works Section */
.how-it-works-section {
  padding: 120px 0;
  background: #ffffff;
  position: relative;
}

.how-it-works-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.02) 0%, rgba(29, 78, 216, 0.02) 100%);
  pointer-events: none;
}

.section-header {
  text-align: center;
  margin-bottom: 80px;
  position: relative;
  z-index: 1;
}

.section-title {
  font-size: 2.75rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 20px;
  letter-spacing: -0.02em;
  position: relative;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  border-radius: 2px;
}

.section-subtitle {
  font-size: 1.2rem;
  color: #4a5568;
  max-width: 650px;
  margin: 0 auto;
  line-height: 1.7;
  font-weight: 400;
}

.step {
  display: flex;
  align-items: flex-start;
  margin-bottom: 40px;
  padding: 35px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #e2e8f0;
  position: relative;
  overflow: hidden;
}

.step::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 5px;
  height: 100%;
  background: linear-gradient(180deg, #3b82f6, #1d4ed8);
  transform: scaleY(0);
  transition: transform 0.4s ease;
}

.step:hover::before {
  transform: scaleY(1);
}

.step:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border-color: #3b82f6;
}

.step-icon-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 25px;
  flex-shrink: 0;
}

.step-number {
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 10px;
}

.step-icon {
  background: #f8f9fa;
  color: #007bff;
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #e9ecef;
}

.step-icon i {
  font-size: 24px;
}

.step-content {
  flex: 1;
}

.step-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 12px;
}

.step-description {
  color: #6c757d;
  line-height: 1.7;
  font-size: 1rem;
  font-weight: 400;
}

/* Commission Transparency */
.commission-transparency {
  background: #ffffff;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  position: relative;
  overflow: hidden;
}

.commission-transparency::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #059669, #047857);
}

.transparency-header {
  margin-bottom: 35px;
  text-align: center;
}

.transparency-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 8px;
  color: #1a202c;
  letter-spacing: -0.01em;
}

.transparency-subtitle {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
}

.commission-calculator {
  width: 100%;
}

.calculator-input {
  margin-bottom: 30px;
  text-align: center;
}

.input-label {
  display: block;
  font-size: 0.9rem;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.price-display {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a202c;
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  padding: 20px;
  border-radius: 12px;
  border: 2px solid #e2e8f0;
}

.commission-breakdown {
  margin-bottom: 25px;
}

.breakdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px;
  margin-bottom: 12px;
  border-radius: 12px;
  transition: all 0.3s ease;
  border: 1px solid #e5e7eb;
}

.breakdown-item:hover {
  transform: translateX(5px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.dealer-item {
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  border-color: #a7f3d0;
}

.platform-item {
  background: linear-gradient(135deg, #eff6ff, #dbeafe);
  border-color: #93c5fd;
}

.agent-item {
  background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
  border-color: #7dd3fc;
}

.referral-item {
  background: linear-gradient(135deg, #fefce8, #fef3c7);
  border-color: #fde68a;
}

.breakdown-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.breakdown-label {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
}

.breakdown-percentage {
  font-size: 0.85rem;
  color: #6b7280;
  font-weight: 500;
}

.breakdown-value {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1a202c;
}

.total-summary {
  border-top: 2px solid #e5e7eb;
  padding-top: 20px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: linear-gradient(135deg, #f9fafb, #f3f4f6);
  border-radius: 12px;
  border: 1px solid #d1d5db;
}

.summary-label {
  font-size: 1.1rem;
  font-weight: 600;
  color: #374151;
}

.summary-value {
  font-size: 1.4rem;
  font-weight: 700;
  color: #dc2626;
}

/* Professional CTA Section */
.cta-section {
  padding: 100px 0;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  position: relative;
}

.cta-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="cta-pattern" width="40" height="40" patternUnits="userSpaceOnUse"><path d="M0 40L40 0H20L0 20M40 40V20L20 40" fill="%23e2e8f0" opacity="0.3"/></pattern></defs><rect width="100" height="100" fill="url(%23cta-pattern)"/></svg>');
  pointer-events: none;
}

.cta-content {
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.cta-title {
  font-size: 3rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 20px;
  letter-spacing: -0.02em;
}

.cta-subtitle {
  font-size: 1.3rem;
  color: #4a5568;
  margin-bottom: 40px;
  line-height: 1.6;
}

.cta-features {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-bottom: 50px;
  flex-wrap: wrap;
}

.cta-feature {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #059669;
  font-weight: 600;
}

.cta-feature i {
  font-size: 24px;
  color: #059669;
}

.cta-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}

.cta-primary-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
  color: white !important;
  padding: 16px 32px !important;
  font-size: 1.1rem !important;
  font-weight: 600 !important;
  border-radius: 12px !important;
  text-transform: none !important;
  box-shadow: 0 10px 15px -3px rgba(59, 130, 246, 0.4) !important;
  transition: all 0.3s ease !important;
}

.cta-primary-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 20px 25px -5px rgba(59, 130, 246, 0.4) !important;
}

.cta-secondary-btn {
  border: 2px solid #3b82f6 !important;
  color: #3b82f6 !important;
  padding: 14px 30px !important;
  font-size: 1.1rem !important;
  font-weight: 600 !important;
  border-radius: 12px !important;
  text-transform: none !important;
  transition: all 0.3s ease !important;
}

.cta-secondary-btn:hover {
  background: #3b82f6 !important;
  color: white !important;
  transform: translateY(-2px) !important;
}

/* Testimonials Section */
.testimonials-section {
  padding: 120px 0;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  position: relative;
  overflow: hidden;
}

.testimonials-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="testimonial-grid" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="1" fill="%23ffffff" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23testimonial-grid)"/></svg>');
  pointer-events: none;
}

.testimonials-section .v-container {
  position: relative;
  z-index: 1;
}

.testimonial-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 40px 30px;
  height: 100%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
}

.testimonial-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #f59e0b, #d97706);
}

.testimonial-card:hover {
  transform: translateY(-10px) scale(1.02);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  background: rgba(255, 255, 255, 1);
}

.testimonial-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.testimonial-avatar img {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  margin-right: 15px;
}

.testimonial-name {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 5px;
  color: #2c3e50;
}

.testimonial-rating {
  display: flex;
  gap: 2px;
}

.testimonial-text {
  color: #34495e;
  line-height: 1.6;
  font-style: italic;
}

/* Remove default margins and padding */
.v-container {
  padding: 0 16px !important;
}

.v-row {
  margin: 0 !important;
}

.v-col {
  padding: 12px !important;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .hero-container {
    padding: 60px 20px;
  }

  .hero-title {
    font-size: 3.5rem;
  }
}

@media (max-width: 960px) {
  .hero-section {
    min-height: auto;
    padding: 80px 0;
  }

  .hero-content {
    text-align: center;
    margin-bottom: 40px;
  }

  .hero-title {
    font-size: 2.8rem;
  }

  .hero-visual-container {
    height: 400px;
    gap: 20px;
  }

  .stats-dashboard {
    padding: 20px;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }

  .hero-car-display {
    padding: 30px;
  }

  .hero-car-image {
    max-width: 350px;
  }

  .car-commission {
    font-size: 1.2rem;
  }
}

@media (max-width: 768px) {
  .hero-content-wrapper {
    gap: 30px;
    min-height: 70vh;
  }

  .commission-title {
    font-size: 2rem;
  }

  .commission-example {
    font-size: 1.2rem;
  }

  .hero-car-image {
    max-width: 350px;
  }

  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.1rem;
  }

  .hero-visual-container {
    height: 350px;
    gap: 15px;
    padding: 15px;
  }

  .stats-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .stat-value {
    font-size: 1.5rem;
  }

  .hero-car-display {
    padding: 25px;
  }

  .hero-car-image {
    max-width: 300px;
  }

  .car-type {
    font-size: 1rem;
  }

  .car-commission {
    font-size: 1.1rem;
  }

  .section-title {
    font-size: 2rem;
    text-align: center;
  }

  .features-section {
    padding: 60px 0;
  }

  .feature-card {
    margin-bottom: 20px;
    padding: 40px 25px;
  }

  .cta-title {
    font-size: 2.5rem;
  }

  .cta-features {
    gap: 25px;
  }

  .cta-actions {
    flex-direction: column;
    align-items: center;
  }

  .cta-primary-btn, .cta-secondary-btn {
    width: 100%;
    max-width: 300px;
  }

  .breakdown-item {
    padding: 15px;
  }

  .price-display {
    font-size: 2rem;
    padding: 15px;
  }
}

@media (max-width: 600px) {
  .hero-title {
    font-size: 2rem;
  }

  .hero-buttons {
    flex-direction: column;
    gap: 12px;
  }

  .hero-cta-btn,
  .hero-login-btn {
    width: 100%;
    max-width: 280px;
  }

  .hero-visual-container {
    height: 380px;
    gap: 10px;
    padding: 10px;
  }

  .stats-dashboard {
    padding: 15px;
  }

  .dashboard-header h4 {
    font-size: 1rem;
  }

  .stat-value {
    font-size: 1.3rem;
  }

  .stat-label {
    font-size: 0.75rem;
  }

  .hero-car-display {
    padding: 20px;
  }

  .hero-car-image {
    max-width: 250px;
  }

  .car-type {
    font-size: 0.9rem;
  }

  .car-commission {
    font-size: 1rem;
  }

  .cta-title {
    font-size: 2rem;
  }

  .cta-subtitle {
    font-size: 1.1rem;
  }

  .cta-features {
    flex-direction: column;
    gap: 15px;
    align-items: center;
  }

  .breakdown-item {
    padding: 12px 15px;
  }

  .breakdown-label {
    font-size: 0.9rem;
  }

  .breakdown-value {
    font-size: 1rem;
  }

  .price-display {
    font-size: 1.8rem;
    padding: 12px;
  }

  .testimonial-card {
    padding: 30px 20px;
  }
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  /* General Mobile Styles */
  .dealer-portal .v-container {
    padding: 0 15px !important;
    max-width: 100% !important;
  }

  .dealer-portal .v-main {
    padding: 0 !important;
  }

  /* Improve touch targets */
  .dealer-portal .v-btn {
    min-height: 44px !important;
  }

  /* Better text readability */
  .dealer-portal {
    font-size: 16px !important;
    line-height: 1.5 !important;
  }

  /* Hero Section Mobile */
  .hero-section {
    padding: 20px 0;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  }

  .hero-container {
    padding: 20px 15px;
  }

  .hero-content {
    text-align: center;
    padding: 0 10px;
    margin-bottom: 30px;
  }

  .hero-title {
    font-size: 2.2rem !important;
    line-height: 1.1 !important;
    margin-bottom: 20px !important;
  }

  .hero-subtitle {
    font-size: 1.1rem !important;
    margin-bottom: 25px !important;
    max-width: 100% !important;
  }

  .hero-subtitle br {
    display: none;
  }

  .hero-buttons {
    justify-content: center !important;
    margin-bottom: 20px;
  }

  .hero-cta-btn {
    width: 100% !important;
    max-width: 280px !important;
    padding: 14px 24px !important;
    font-size: 1rem !important;
    min-height: 48px !important;
    border-radius: 25px !important;
  }

  /* Hero Visual Mobile */
  .hero-visual {
    order: -1;
    margin-bottom: 20px;
  }

  .big-car-display {
    padding: 20px 15px !important;
    margin: 0 10px;
  }

  .showcase-car-image {
    max-width: 280px !important;
    margin-bottom: 15px !important;
  }

  .commission-info h3 {
    font-size: 1.1rem !important;
    margin-bottom: 8px !important;
  }

  .commission-amount {
    font-size: 2.2rem !important;
    margin-bottom: 3px !important;
  }

  .commission-info p {
    font-size: 0.95rem !important;
  }

  /* Features Section Mobile */
  .features-section {
    padding: 60px 0 !important;
  }

  .feature-col {
    padding: 10px !important;
    margin-bottom: 20px;
  }

  .feature-card {
    padding: 30px 20px !important;
  }

  .feature-icon {
    margin-bottom: 20px !important;
  }

  .feature-title {
    font-size: 1.3rem !important;
    margin-bottom: 15px !important;
  }

  .feature-description {
    font-size: 0.95rem !important;
    max-width: 100% !important;
  }

  /* How It Works Section Mobile */
  .how-it-works-section {
    padding: 80px 0 !important;
  }

  .section-header {
    margin-bottom: 50px !important;
    padding: 0 15px;
  }

  .section-title {
    font-size: 2.2rem !important;
    margin-bottom: 15px !important;
  }

  .section-subtitle {
    font-size: 1.1rem !important;
    max-width: 100% !important;
  }

  .step {
    flex-direction: column !important;
    text-align: center !important;
    padding: 25px 20px !important;
    margin-bottom: 25px !important;
  }

  .step-icon-wrapper {
    margin-right: 0 !important;
    margin-bottom: 20px !important;
  }

  .step-title {
    font-size: 1.2rem !important;
    margin-bottom: 10px !important;
  }

  .step-description {
    font-size: 0.95rem !important;
  }

  /* Commission Transparency Mobile */
  .commission-transparency {
    padding: 25px 20px !important;
    margin-top: 30px;
  }

  .transparency-title {
    font-size: 1.4rem !important;
  }

  .price-display {
    font-size: 2rem !important;
    padding: 15px !important;
  }

  .breakdown-item {
    padding: 15px !important;
    margin-bottom: 10px !important;
  }

  .breakdown-label {
    font-size: 0.9rem !important;
  }

  .breakdown-value {
    font-size: 1.1rem !important;
  }

  /* CTA Section Mobile */
  .cta-section {
    padding: 60px 0 !important;
  }

  .cta-content {
    padding: 0 15px;
  }

  .cta-title {
    font-size: 2rem !important;
    margin-bottom: 15px !important;
  }

  .cta-subtitle {
    font-size: 1.1rem !important;
    margin-bottom: 25px !important;
  }

  .cta-features {
    flex-direction: column !important;
    gap: 15px !important;
    margin-bottom: 30px !important;
  }

  .cta-actions {
    flex-direction: column !important;
    gap: 15px !important;
    align-items: center !important;
  }

  .cta-primary-btn,
  .cta-secondary-btn {
    width: 100% !important;
    max-width: 280px !important;
    padding: 14px 24px !important;
    font-size: 1rem !important;
    min-height: 48px !important;
    border-radius: 25px !important;
  }

  /* Testimonials Mobile */
  .testimonials-section {
    padding: 60px 0 !important;
  }

  .testimonial-col {
    margin-bottom: 20px;
  }

  .testimonial-card {
    padding: 25px 20px !important;
  }

  .testimonial-name {
    font-size: 1.1rem !important;
  }

  .testimonial-text {
    font-size: 0.95rem !important;
  }
}

@media (max-width: 480px) {
  /* Extra Small Mobile */
  .hero-content-wrapper {
    gap: 20px;
    min-height: 60vh;
  }

  .commission-title {
    font-size: 1.6rem;
  }

  .commission-example {
    font-size: 1rem;
  }

  .hero-car-image {
    max-width: 280px;
  }

  .hero-title {
    font-size: 1.8rem !important;
  }

  .hero-subtitle {
    font-size: 1rem !important;
  }

  .hero-cta-btn {
    padding: 12px 20px !important;
    font-size: 0.9rem !important;
    min-height: 44px !important;
  }

  .hero-container {
    padding: 15px 10px !important;
  }

  .showcase-car-image {
    max-width: 240px !important;
  }

  .commission-amount {
    font-size: 1.8rem !important;
  }

  .section-title {
    font-size: 1.8rem !important;
  }

  .feature-card {
    padding: 25px 15px !important;
  }

  .step {
    padding: 20px 15px !important;
  }

  .commission-transparency {
    padding: 20px 15px !important;
  }

  .cta-title {
    font-size: 1.6rem !important;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  /* Tablet Styles */
  .hero-title {
    font-size: 2.8rem !important;
  }

  .hero-subtitle {
    font-size: 1.2rem !important;
  }

  .showcase-car-image {
    max-width: 320px !important;
  }

  .section-title {
    font-size: 2.4rem !important;
  }

  .feature-card {
    padding: 40px 25px !important;
  }
}

/* Contact Section */
.contact-section {
  background: linear-gradient(135deg, #1a202c 0%, #2d3748 100%);
  padding: 80px 0;
  color: white;
  text-align: center;
}

.contact-content {
  max-width: 600px;
  margin: 0 auto;
}

.contact-title {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 40px;
  color: white;
  font-family: 'Inter', sans-serif;
}

.contact-buttons {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 60px;
  align-items: center;
}

.contact-btn {
  width: 280px;
  height: 60px;
  border-radius: 30px;
  font-size: 1.1rem;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.call-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
  color: white !important;
}

.call-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
}

.call-btn:disabled {
  opacity: 0.6;
  transform: none;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.call-btn.call-active {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
  }
  50% {
    box-shadow: 0 6px 25px rgba(239, 68, 68, 0.6);
  }
  100% {
    box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
  }
}

.chat-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
}

.chat-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.btn-icon {
  margin-right: 8px;
  font-size: 1.2rem;
}

.quick-links, .services-links {
  margin-bottom: 40px;
}

.quick-links-title, .services-links-title {
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 20px;
  color: white;
  letter-spacing: 1px;
  font-family: 'Inter', sans-serif;
}

.quick-links-list, .services-links-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

.quick-link {
  color: #cbd5e1;
  text-decoration: none;
  font-size: 1rem;
  transition: all 0.3s ease;
  font-family: 'Inter', sans-serif;
  padding: 8px 16px;
  border-radius: 8px;
}

.quick-link:hover {
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
  text-decoration: none;
}

.service-link {
  color: #cbd5e1;
  font-size: 1rem;
  font-family: 'Inter', sans-serif;
}

.voice-agent-status {
  margin-top: 20px;
  padding: 15px 20px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 12px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.status-text {
  color: #cbd5e1;
  font-size: 0.9rem;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-family: 'Inter', sans-serif;
}

.status-icon {
  margin-right: 8px;
  color: #3b82f6;
  font-size: 1.1rem;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .contact-section {
    padding: 60px 0;
  }

  .contact-title {
    font-size: 2.5rem;
    margin-bottom: 30px;
  }

  .contact-btn {
    width: 250px;
    height: 55px;
    font-size: 1rem;
  }

  .contact-buttons {
    margin-bottom: 40px;
  }
}

@media (max-width: 480px) {
  .contact-title {
    font-size: 2rem;
  }

  .contact-btn {
    width: 220px;
    height: 50px;
    font-size: 0.95rem;
  }
}

/* Smooth scrolling */
html {
  scroll-behavior: smooth;
}
</style>
